/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Raw Hotspots family (E.W.0.5,
 *  operator-cop-doctrine §3A.7 / §3B.8 the AMBIENT register / §9.1 raw thermal
 *  is a pre-fusion, lower-confidence signal).
 *
 *  This module is the SINGLE source of the RAW (pre-fusion) satellite-thermal
 *  palette. It is consumed ONLY by the raw-hotspots family:
 *    - RawHotspotsLayer map markers (the ambient source-coloured dot)
 *    - the RawHotspotBody side-panel source badge + legend
 *
 *  Raw hotspots are AMBIENT and LOW-CONFIDENCE — deliberately DESATURATED so they
 *  read as quiet context, never competing with the vivid Response-Level Anomaly-
 *  Queue dots (styles/responseLevel) that the F.1 fusion engine produces. SHAPE
 *  is shared across the family (one small ambient dot, smaller than the smallest
 *  L1 anomaly dot and NEVER pulsing); COLOR encodes the SOURCE so an operator can
 *  read which satellite/system saw the heat:
 *    firms-nrt    muted sand-amber   #c7a36a   (NASA FIRMS polar VIIRS/MODIS)
 *    cwfis-m3     muted slate        #8aa1ac   (CWFIS M3 polar)
 *    goes-r-fdcc  muted bronze       #b08055   (GOES-R geostationary, CONUS)
 *    goes-r-fdcf  muted copper       #9c6f4a   (GOES-R geostationary, Full Disk / north)
 *    (other)      neutral grey       #9ca3af   (unknown source, never dropped)
 *
 *  Overlap debt this lock acknowledges (§3A.7): these muted hues are DELIBERATELY
 *  desaturated cousins of tokens that mean other things in OTHER families — amber
 *  (#f59e0b) = IMS Response Level 3 / OC-Monitored fire / severe-storm CAP; grey
 *  (#94a3b8) = Response Level 1. Those families own their own vivid hexes; this
 *  module owns its OWN muted hexes and must never import a sibling palette. Colour
 *  is never load-bearing alone: every render is paired with the source label
 *  (panel + legend), exactly like the community-status pattern.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus (enforced by the no-restricted-imports lint rule in
 *     eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** The per-source muted hexes — the single source of truth. */
export const RAW_HOTSPOT_SOURCE_COLORS: Record<string, string> = {
  'firms-nrt': '#c7a36a', // muted sand-amber
  'cwfis-m3': '#8aa1ac', // muted slate
  'goes-r-fdcc': '#b08055', // muted bronze (GOES-R CONUS)
  'goes-r-fdcf': '#9c6f4a', // muted copper (GOES-R Full Disk / northern)
};

/** Neutral ambient grey for an unknown / unmapped source (never dropped). */
export const RAW_HOTSPOT_DEFAULT_COLOR = '#9ca3af';

/** Short human label per source, paired with the colour so hue is never alone. */
export const RAW_HOTSPOT_SOURCE_LABEL: Record<string, string> = {
  'firms-nrt': 'FIRMS NRT (VIIRS / MODIS)',
  'cwfis-m3': 'CWFIS M3',
  'goes-r-fdcc': 'GOES-R FDCC',
  'goes-r-fdcf': 'GOES-R FDCF (Full Disk / north)',
};

/** Ambient dot diameter (px) — smaller than the L1 anomaly dot (10px) so raw
 *  thermal never competes with the scored Anomaly-Queue dots. */
export const RAW_HOTSPOT_DOT_SIZE = 7;
/** Fill opacity — low, reinforcing the quiet ambient read. */
export const RAW_HOTSPOT_DOT_ALPHA = 0.7;

export interface RawHotspotStyle {
  /** CSS hex for the dot + badge. */
  color: string;
  /** Source label — colour is never load-bearing alone. */
  label: string;
}

/**
 * Resolve the render style for a raw hotspot's source. Tolerant of null /
 * unknown (→ the neutral grey ambient style + the raw source string as its own
 * label, never dropped).
 */
export function rawHotspotStyle(source: string | null | undefined): RawHotspotStyle {
  const key = source ?? '';
  return {
    color: RAW_HOTSPOT_SOURCE_COLORS[key] ?? RAW_HOTSPOT_DEFAULT_COLOR,
    label: RAW_HOTSPOT_SOURCE_LABEL[key] ?? (source || 'Unknown source'),
  };
}
